import { first, isNotEmptyArray, now } from '@vy/utils';
import { INTEGRATION_TYPES } from '@vy/integrations-types';
import {
  type NetSuiteClass,
  ProductIntegrationEntity,
  SYNC_STATUSES,
  type AnrokProductEntity,
  type NetSuiteProductEntity,
  type PaddleProductEntity,
  type QuickBooksProductEntity,
  type XeroProductEntity,
} from '@vy/types';

type ErpProductEntity = NetSuiteProductEntity | QuickBooksProductEntity | XeroProductEntity | PaddleProductEntity;

export function buildNetSuiteIntegrationEntities(
  netSuiteProducts?: NetSuiteProductEntity[],
  netSuiteClass?: NetSuiteClass,
): ProductIntegrationEntity[] {
  if (!isNotEmptyArray(netSuiteProducts)) {
    return [];
  }

  const product = first(netSuiteProducts);

  return [
    {
      integrationProvider: INTEGRATION_TYPES.NETSUITE,
      integrationExternalId: product.integrationExternalId,
      syncStatus: SYNC_STATUSES.Synced,
      updatedAt: now(),
      metadata: {
        name: product.metadata?.name,
        displayId: product.metadata?.displayId,
        classMetadata: netSuiteClass ?? undefined,
      },
    },
  ];
}

export function buildQuickBooksIntegrationEntities(
  quickBooksProducts?: QuickBooksProductEntity[],
): ProductIntegrationEntity[] {
  return buildErpIntegrationEntities(INTEGRATION_TYPES.QUICKBOOKS, quickBooksProducts);
}

export function buildXeroIntegrationEntities(
  xeroProducts?: XeroProductEntity[],
): ProductIntegrationEntity[] {
  return buildErpIntegrationEntities(INTEGRATION_TYPES.XERO, xeroProducts);
}

export function buildPaddleIntegrationEntities(
  paddleProducts?: PaddleProductEntity[],
): ProductIntegrationEntity[] {
  return buildErpIntegrationEntities(INTEGRATION_TYPES.PADDLE, paddleProducts);
}

export function buildAnrokIntegrationEntities(
  anrokProducts?: AnrokProductEntity[],
): ProductIntegrationEntity[] {
  if (!isNotEmptyArray(anrokProducts)) {
    return [];
  }

  const anrokProduct = first(anrokProducts);

  const metadata = buildAnrokMetadata(anrokProduct);

  return [
    {
      integrationProvider: INTEGRATION_TYPES.ANROK,
      integrationExternalId: anrokProduct.integrationExternalId,
      syncStatus: SYNC_STATUSES.Synced,
      updatedAt: now(),
      metadata,
    },
  ];
}

function buildAnrokMetadata(anrokProduct: AnrokProductEntity) {
  const productId = anrokProduct.metadata?.productId;

  if (!productId) {
    return undefined;
  }

  return { productId };
}

function buildErpIntegrationEntities(
  provider: ErpProductEntity['integrationProvider'],
  products?: ErpProductEntity[],
): ProductIntegrationEntity[] {
  if (!isNotEmptyArray(products)) {
    return [];
  }

  const product = first(products);

  return [
    {
      integrationProvider: provider,
      integrationExternalId: product.integrationExternalId,
      syncStatus: SYNC_STATUSES.Synced,
      updatedAt: now(),
      metadata: {
        name: product.metadata?.name,
        displayId: product.metadata?.displayId,
      },
    },
  ];
}
